import { useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { PHONE_BRANDS, OPERATORS, INTERESTS } from "@/lib/constants";
import type { Member } from "@/lib/data-hooks";

export type Store = { id: string; name: string };

export function useMemberFilter(members: Member[]) {
  const [q, setQ] = useState("");
  const [storeF, setStoreF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [brandF, setBrandF] = useState("");
  const [opF, setOpF] = useState("");
  const [genderF, setGenderF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [sourceF, setSourceF] = useState("");
  const [cityF, setCityF] = useState("");
  const [gdprF, setGdprF] = useState("");
  const [interestF, setInterestF] = useState<string[]>([]);

  const filtered = useMemo(
    () =>
      members.filter((c) => {
        const hay = `${c.first_name} ${c.last_name} ${c.member_number} ${c.email} ${c.phone ?? ""} ${c.ssn ?? ""} ${c.address ?? ""} ${c.city ?? ""} ${c.zip ?? ""} ${c.notes ?? ""} ${c.interests_text ?? ""} ${(c.interests ?? []).join(" ")}`.toLowerCase();
        if (q && !hay.includes(q.toLowerCase())) return false;
        if (storeF && c.store_id !== storeF) return false;
        if (statusF && c.status !== statusF) return false;
        if (brandF && c.phone_brand !== brandF) return false;
        if (opF && c.operator !== opF) return false;
        if (genderF && c.gender !== genderF) return false;
        if (typeF && c.customer_type !== typeF) return false;
        if (sourceF && c.source !== sourceF) return false;
        if (cityF && c.city !== cityF) return false;
        if (gdprF === "ja" && !c.gdpr_consent) return false;
        if (gdprF === "nej" && c.gdpr_consent) return false;
        if (interestF.length && !interestF.every((i) => (c.interests ?? []).includes(i))) return false;
        return true;
      }),
    [q, storeF, statusF, brandF, opF, genderF, typeF, sourceF, cityF, gdprF, interestF, members]
  );

  const state = { q, storeF, statusF, brandF, opF, genderF, typeF, sourceF, cityF, gdprF, interestF };
  const setters = { setQ, setStoreF, setStatusF, setBrandF, setOpF, setGenderF, setTypeF, setSourceF, setCityF, setGdprF, setInterestF };
  const clear = () => {
    setQ(""); setStoreF(""); setStatusF(""); setBrandF(""); setOpF("");
    setGenderF(""); setTypeF(""); setSourceF(""); setCityF(""); setGdprF(""); setInterestF([]);
  };
  return { state, setters, filtered, clear };
}

export function MemberFilterPanel({
  state, setters, stores, members, filteredCount, onClear, searchPlaceholder,
}: {
  state: ReturnType<typeof useMemberFilter>["state"];
  setters: ReturnType<typeof useMemberFilter>["setters"];
  stores: Store[];
  members: Member[];
  filteredCount: number;
  onClear: () => void;
  searchPlaceholder?: string;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const cities = useMemo(
    () => Array.from(new Set(members.map((m) => m.city).filter(Boolean))).sort() as string[],
    [members]
  );
  const toggleInterest = (i: string) =>
    setters.setInterestF(state.interestF.includes(i) ? state.interestF.filter((x) => x !== i) : [...state.interestF, i]);

  return (
    <div className="card-soft p-5 space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input className="input-field pl-9" placeholder={searchPlaceholder ?? "Sök i namn, mejl, telefon, personnr, adress, anteckningar..."} value={state.q} onChange={(e) => setters.setQ(e.target.value)} />
        </div>
        <select className="input-field" value={state.storeF} onChange={(e) => setters.setStoreF(e.target.value)}>
          <option value="">Alla butiker</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className="input-field" value={state.statusF} onChange={(e) => setters.setStatusF(e.target.value)}>
          <option value="">Alla status</option>
          <option value="aktiv">Aktiv</option>
          <option value="inaktiv">Inaktiv</option>
          <option value="pausad">Pausad</option>
          <option value="uppsagd">Uppsagd</option>
        </select>
        <select className="input-field" value={state.brandF} onChange={(e) => setters.setBrandF(e.target.value)}>
          <option value="">Alla telefonmärken</option>
          {PHONE_BRANDS.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select className="input-field" value={state.opF} onChange={(e) => setters.setOpF(e.target.value)}>
          <option value="">Alla operatörer</option>
          {OPERATORS.map((o) => <option key={o}>{o}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="input-field flex items-center justify-center gap-2 text-sm font-display hover:bg-muted md:col-span-2"
        >
          <SlidersHorizontal size={14} />
          {showAdvanced ? "Dölj fler filter" : "Fler filter"}
        </button>
      </div>

      {showAdvanced && (
        <div className="pt-4 border-t border-border space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <select className="input-field" value={state.genderF} onChange={(e) => setters.setGenderF(e.target.value)}>
              <option value="">Alla kön</option>
              <option>Kvinna</option><option>Man</option><option>Annat</option>
            </select>
            <select className="input-field" value={state.typeF} onChange={(e) => setters.setTypeF(e.target.value)}>
              <option value="">Privat & Företag</option>
              <option>Privat</option><option>Företag</option>
            </select>
            <select className="input-field" value={state.sourceF} onChange={(e) => setters.setSourceF(e.target.value)}>
              <option value="">Alla källor</option>
              <option>I butik</option><option>Vän/familj</option><option>Sociala medier</option><option>Webben</option>
            </select>
            <select className="input-field" value={state.cityF} onChange={(e) => setters.setCityF(e.target.value)}>
              <option value="">Alla orter</option>
              {cities.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className="input-field" value={state.gdprF} onChange={(e) => setters.setGdprF(e.target.value)}>
              <option value="">GDPR – alla</option>
              <option value="ja">Samtycke givet</option>
              <option value="nej">Inget samtycke</option>
            </select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-display">Vill lära sig mer om</div>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => {
                const on = state.interestF.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleInterest(i)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-muted"}`}
                  >
                    {i}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{filteredCount} av {members.length} kunder</span>
        <button type="button" onClick={onClear} className="text-primary hover:underline font-display">Rensa filter</button>
      </div>
    </div>
  );
}
